import { useState } from "react"
import { useForm, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, Pencil, Save, Trash2 } from "lucide-react"

interface Note {
  id: number
  content: string
  date: string
  user_id: number
  user?: {
    username: string
  }
}

interface NotesListProps {
  patientId: string
  notes: Note[]
}

export default function NotesList({ patientId, notes }: NotesListProps) {
  const [isCreating, setIsCreating] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)

  // Form for creating
  const createForm = useForm({
    content: "",
    date: new Date().toISOString().split('T')[0]
  })

  // Form for editing
  const editForm = useForm({
    content: "",
    date: ""
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    createForm.post(`/patients/${patientId}/notes`, {
      onSuccess: () => {
        setIsCreating(false)
        createForm.reset()
      }
    })
  }

  const startEdit = (note: Note) => {
    editForm.setData({
      content: note.content,
      date: note.date
    })
    setEditingNoteId(note.id)
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingNoteId) return

    editForm.patch(`/patients/${patientId}/notes/${editingNoteId}`, {
      onSuccess: () => setEditingNoteId(null)
    })
  }

  const handleDelete = (noteId: number) => {
    if (confirm("Êtes-vous sûr de vouloir supprimer cette note ?")) {
      router.delete(`/patients/${patientId}/notes/${noteId}`)
    }
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xl">Notes</CardTitle>
        {!isCreating && (
          <Button size="sm" onClick={() => setIsCreating(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex-1 overflow-auto space-y-4">
        {isCreating && (
          <form onSubmit={handleCreate} className="space-y-3 p-3 border rounded-md bg-muted/20">
            <div className="space-y-1">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={createForm.data.date}
                onChange={e => createForm.setData("date", e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="content">Note</Label>
              <Textarea
                id="content"
                value={createForm.data.content}
                onChange={e => createForm.setData("content", e.target.value)}
                required
                className="min-h-[100px]"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => setIsCreating(false)}>
                Annuler
              </Button>
              <Button type="submit" size="sm" disabled={createForm.processing}>
                Enregistrer
              </Button>
            </div>
          </form>
        )}

        {notes.length === 0 && !isCreating && (
           <p className="text-center text-muted-foreground py-8">Aucune note.</p>
        )}

        {notes.map(note => (
          <div key={note.id} className="p-3 border rounded-md space-y-2">
            {editingNoteId === note.id ? (
              <form onSubmit={handleUpdate} className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor={`edit-date-${note.id}`}>Date</Label>
                  <Input
                    id={`edit-date-${note.id}`}
                    type="date"
                    value={editForm.data.date}
                    onChange={e => editForm.setData("date", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1">
                   <Label htmlFor={`edit-content-${note.id}`}>Note</Label>
                  <Textarea
                    id={`edit-content-${note.id}`}
                    value={editForm.data.content}
                    onChange={e => editForm.setData("content", e.target.value)}
                    required
                  />
                </div>
                 <div className="flex gap-2 justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEditingNoteId(null)}>
                    Annuler
                  </Button>
                  <Button type="submit" size="sm" disabled={editForm.processing}>
                    <Save className="w-4 h-4 mr-1" />
                    Enregistrer
                  </Button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex justify-between items-start">
                  <span className="font-semibold text-sm">
                    {new Date(note.date).toLocaleDateString()}
                  </span>
                   <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => startEdit(note)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" onClick={() => handleDelete(note.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                   </div>
                </div>
                <div className="text-sm whitespace-pre-wrap">{note.content}</div>
                <div className="text-xs text-muted-foreground mt-2">
                  Par {note.user?.username || "Inconnu"}
                </div>
              </>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
